import { z } from 'zod';
import { Id, IsoDate } from './common';

/**
 * Enrichissements d'un cluster exposés par `GET /clusters/:id` (fiches T17–T20) : snapshots
 * SERP avec leurs items classés, analyses LLM (liste et lecture complète), suggestions de fusion ;
 * et les corps des actions `merge` / `dismiss` (`api.md` § Mots-clés et clusters).
 */

export const SerpItemType = z.enum([
  'organic',
  'paid',
  'featured_snippet',
  'people_also_ask',
  'video',
  'images',
  'shopping',
  'local_pack',
  'knowledge_graph',
  'related_searches',
  'other',
]);
export type SerpItemType = z.infer<typeof SerpItemType>;

export const ResultClass = z.enum([
  'tool',
  'saas',
  'article',
  'forum',
  'marketplace',
  'directory',
  'brand',
  'video',
  'government',
  'other',
]);
export type ResultClass = z.infer<typeof ResultClass>;

export const SerpStatus = z.enum(['pending', 'ready', 'failed']);
export type SerpStatus = z.infer<typeof SerpStatus>;

export const ClusterSnapshotItem = z.object({
  id: z.number().int(),
  type: SerpItemType,
  rankGroup: z.number().int(),
  rankAbsolute: z.number().int(),
  domain: z.string().nullable(),
  url: z.string().nullable(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  classification: ResultClass.nullable(),
  /** `rule` ou `llm` ; `null` tant que l'item n'est pas classé. */
  classificationSource: z.string().nullable(),
  /** Petites données du bloc (`questions` d'un PAA, `count` d'un carrousel vidéo…). */
  data: z.record(z.string(), z.unknown()).nullable(),
});
export type ClusterSnapshotItem = z.infer<typeof ClusterSnapshotItem>;

export const SerpFeatures = z.object({
  has_featured_snippet: z.boolean(),
  paa_questions: z.array(z.string()),
  has_video: z.boolean(),
  has_shopping: z.boolean(),
});
export type SerpFeatures = z.infer<typeof SerpFeatures>;

export const ClusterSnapshot = z.object({
  id: Id,
  keywordId: z.number().int(),
  keywordText: z.string(),
  scanId: Id.nullable(),
  mode: z.string(),
  status: SerpStatus,
  fetchedAt: IsoDate.nullable(),
  seResultsCount: z.number().nullable(),
  itemTypes: z.array(z.string()),
  organicCount: z.number().int(),
  paidCount: z.number().int(),
  paidTopCount: z.number().int(),
  features: SerpFeatures,
  error: z.string().nullable(),
  createdAt: IsoDate,
  items: z.array(ClusterSnapshotItem),
});
export type ClusterSnapshot = z.infer<typeof ClusterSnapshot>;

export const LlmTier = z.enum(['triage', 'deep']);
export type LlmTier = z.infer<typeof LlmTier>;

export const LlmStatus = z.enum(['ok', 'invalid', 'failed']);
export type LlmStatus = z.infer<typeof LlmStatus>;

export const LlmUsage = z.object({
  input_tokens: z.number(),
  cached_tokens: z.number(),
  output_tokens: z.number(),
});
export type LlmUsage = z.infer<typeof LlmUsage>;

/** Ligne de la liste `analyses[]` d'un cluster (sans `input` ni `output`). */
export const ClusterAnalysisSummary = z.object({
  id: Id,
  scanId: Id.nullable(),
  tier: LlmTier,
  model: z.string(),
  promptVersion: z.string(),
  status: LlmStatus,
  inputHash: z.string(),
  usage: LlmUsage,
  costUsd: z.number(),
  latencyMs: z.number().int(),
  error: z.string().nullable(),
  createdAt: IsoDate,
});
export type ClusterAnalysisSummary = z.infer<typeof ClusterAnalysisSummary>;

/** `GET /clusters/:id/analyses/:analysisId` : entrée (pack de preuves) et sortie complètes. */
export const ClusterAnalysisDetail = ClusterAnalysisSummary.extend({
  clusterId: Id,
  input: z.record(z.string(), z.unknown()),
  output: z.record(z.string(), z.unknown()).nullable(),
  rawOutput: z.string().nullable(),
});
export type ClusterAnalysisDetail = z.infer<typeof ClusterAnalysisDetail>;

export const MergeSuggestionStatus = z.enum(['open', 'accepted', 'dismissed']);
export type MergeSuggestionStatus = z.infer<typeof MergeSuggestionStatus>;

/** Suggestion vue depuis un cluster : l'autre cluster de la paire et le recouvrement d'URL. */
export const ClusterMergeSuggestion = z.object({
  id: Id,
  otherClusterId: Id,
  otherLabel: z.string(),
  /** `true` si le cluster consulté est `cluster_a_id` de la paire. */
  isA: z.boolean(),
  overlap: z.number(),
  status: MergeSuggestionStatus,
  scanId: Id.nullable(),
  decidedAt: IsoDate.nullable(),
  createdAt: IsoDate,
});
export type ClusterMergeSuggestion = z.infer<typeof ClusterMergeSuggestion>;

export const MERGE_RATIONALE_MAX_LENGTH = 1000;

/** `POST /clusters/:id/merge`. */
export const MergeClusterBody = z.object({
  intoClusterId: Id,
  rationale: z.string().trim().min(1).max(MERGE_RATIONALE_MAX_LENGTH),
});
export type MergeClusterBody = z.infer<typeof MergeClusterBody>;

export const MergeClusterResponse = z.object({
  id: Id,
  intoClusterId: Id,
  keywordsMoved: z.number().int(),
  /** `keyword_count` de la cible après fusion. */
  keywordCount: z.number().int(),
  suggestionId: Id.nullable(),
});
export type MergeClusterResponse = z.infer<typeof MergeClusterResponse>;

/** `POST /clusters/merge-suggestions/:id/dismiss`. */
export const DismissMergeSuggestionResponse = z.object({
  id: Id,
  status: MergeSuggestionStatus,
});
export type DismissMergeSuggestionResponse = z.infer<typeof DismissMergeSuggestionResponse>;
